/**
 * 「TA 的小动作」顶上的场景（TEACH-PAGES-VISUAL-117；r7k 参考稿 01-list-A，I 10-16 选 A 方向）：TA 在自己的小窝里。
 * - 底图：小窝屋内底图（../home/homeStage 的 PLATES.inside），夜里换小窝同一张夜图（plateForSky）。天色只认后端作息，
 *   和小窝同一个读口、同一个查询键（./useTeach 的 useHomeGlance）；读不到当白天，不按本机时钟猜（用户 10-09：小窝夜里切夜景）。
 *   不画新底图、不调付费生图（「TA 的星球生活」也是这样复用小窝底图的）。取景、TA 的远近大小照小窝的舞台算（homeStage 的 FRAME、bodyHeight）。
 * - TA：在家才画。有已启用的教学动作、读口里三段齐：用现成的 PetMotion 站在底姿，进页做一遍最近启用的那个，点 TA 再做一遍
 *   （不循环：方案 9.2 不强制播放，I 10-16 定）。没有能播的动作：PetMotion 自己交给小窝同一个 PetFigure（透明整身图）站着。
 *   减少动态：不自动做，点了 PetMotion 也直接落到稳定姿势。
 * - 夜里 TA 也暗一点，和底图的光一致；睡着了：形象压暗一点、往下沉一点，头顶三个 z，不自动做动作，下面一句「睡着了」——值照小窝
 *   （home.css 的夜里 TA、is-dozing，设计评审第一轮中 6）；出门了：屋里空着，下面一句「出门了」（空屋子要说为什么空）。
 * - 读屏：画面本身不进读屏（底图、形象都是装饰），下面那一句看得见的字就是这块场景的说法；能做动作时那一块是一个按钮「看 TA 再做一遍『…』」，
 *   做的时候另有一个播报区说「在做『…』」。
 * - 参考稿上的装饰英文、墙上手写标语不照抄（I 10-16）。
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { PetPrivateSummary } from "@/shared/contracts";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { FRAME, PLATES, anchorPoint, bodyHeight, plateForSky, plateScale } from "@/features/home/homeStage";
import { usePetCharacter } from "@/features/home/PetFigure";
import { PetMotion } from "@/features/pet_motion/PetMotion";
import type { MotionSet } from "@/features/pet_motion/manifest";
import type { CurrentPet } from "@/features/memories/currentPet";
import { SCENE_AGAIN, SCENE_AWAY, SCENE_ASLEEP, SCENE_DOING, SCENE_PLAIN, SCENE_TAP, headerTitle } from "./teachModel";
import { previewSequence, taughtClips, taughtKey } from "./teachClips";
import type { TeachOrder } from "./teachTypes";
import { useHomeGlance, useTaughtMotion } from "./useTeach";

/** 场景露出的那一块（舞台按宽 780 算，见 homeStage 的 FRAME）：以 TA 落脚那一点为中，露出茶几、地毯和沙发一角，TA 的脚在下面约八成处 */
export const SCENE_VIEW = { left: 205, right: 745, top: 380, bottom: 820 } as const;
/** TA 落脚的那一点：小窝屋里的 1 号窝（地毯前沿） */
export const SCENE_ANCHOR = "inside.rest.1";
const MAIN_LOOPS = 1;

type Box = { left: number; top: number; width: number; height: number };

/** 底图、TA 在场景里的位置（百分比，纯算术，用例直接核） */
export function heroLayout(): { aspect: string; plate: Box; figure: Box } {
  const { left, right, top, bottom } = SCENE_VIEW;
  const w = right - left;
  const h = bottom - top;
  const plate = PLATES.inside;
  const k = plateScale("inside");
  const plateTop = FRAME.top - plate.cropTop * k;
  const foot = anchorPoint("inside", SCENE_ANCHOR);
  const body = bodyHeight("inside", foot.y);
  // PetFigure、PetMotion 的几何：主体高度正好占满容器，落地点对准容器底边中点
  const figureWidth = body * 1.2;
  return {
    aspect: `${w} / ${h}`,
    plate: { left: (-left / w) * 100, width: (FRAME.width / w) * 100, top: ((plateTop - top) / h) * 100, height: ((plate.height * k) / h) * 100 },
    figure: { left: ((foot.x - figureWidth / 2 - left) / w) * 100, width: (figureWidth / w) * 100, top: ((foot.y - body - top) / h) * 100, height: (body / h) * 100 },
  };
}

const pct = (box: Box): CSSProperties => ({ left: `${box.left}%`, top: `${box.top}%`, width: `${box.width}%`, height: `${box.height}%` });

/** 最近启用的那个教学动作（可以用了），没有就 null */
export function latestEnabled(orders: readonly TeachOrder[]): TeachOrder | null {
  const enabled = orders.filter((o) => o.state === "enabled");
  enabled.sort((a, b) => ((b.enabled_at ?? b.updated_at) > (a.enabled_at ?? a.updated_at) ? 1 : (b.enabled_at ?? b.updated_at) < (a.enabled_at ?? a.updated_at) ? -1 : 0));
  return enabled[0] ?? null;
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

/** caption：TA 在家醒着时下面那一句换成这一句（本次学习页说「TA 在学新动作」）；出门、睡着照旧说出门、睡着 */
export function TeachHero({ pet, orders, caption: says }: { pet: CurrentPet; orders: readonly TeachOrder[]; caption?: string }) {
  const household = useOptionalCurrentHousehold();
  const entry = household?.pets.find(({ pet: p }) => p.pet_id === pet.petId) ?? null;
  const character = usePetCharacter(pet.petId);
  const glance = useHomeGlance(pet.petId);
  const move = latestEnabled(orders);
  const motion = useTaughtMotion(pet.petId, Boolean(move));
  const clips = move ? taughtClips(motion.data ?? null, move.order_id) : null;
  const layout = heroLayout();
  const plate = plateForSky("inside", glance.night ? "night" : "day");
  const [target, setTarget] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const played = useRef<string | null>(null);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  const canPlay = Boolean(move && clips && motion.data && !glance.away && !glance.asleep);
  const play = () => {
    if (!move || !clips) return;
    const sequence = previewSequence(clips, MAIN_LOOPS);
    const ms = sequence.slice(1, -2).reduce((sum, clip) => sum + clip.frames.reduce((s, f) => s + f.duration_ms, 0), 0);
    if (timer.current !== null) window.clearTimeout(timer.current);
    setTarget(taughtKey(move.order_id));
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setTarget(clips.basePose);
    }, Math.max(600, ms));
  };
  // 进页做一遍（每个动作只自动做一次；减少动态时不自动做）
  useEffect(() => {
    if (!canPlay || !move || played.current === move.order_id || reducedMotion()) return;
    played.current = move.order_id;
    play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPlay, move?.order_id]);
  const title = move ? headerTitle(move) : null;
  const caption = glance.away ? SCENE_AWAY(pet.name) : glance.asleep ? SCENE_ASLEEP(pet.name) : canPlay && title ? SCENE_TAP(pet.name, title) : says ?? SCENE_PLAIN(pet.name);
  const doing = target && move && target === taughtKey(move.order_id) && title ? SCENE_DOING(pet.name, title) : "";
  const summary: PetPrivateSummary | null = entry ? {
    pet_id: entry.pet.pet_id, home_id: entry.household.home_id ?? null, name: entry.pet.name, species: entry.pet.species, photo_url: entry.pet.photo_url,
    origin: entry.pet.origin, owner_title: null, presence: entry.pet.presence, photo_version: entry.pet.photo_version ?? null,
  } : null;
  const showFigure = Boolean(summary && glance.settled && !glance.away);
  return (
    <section className="ps-teach-scene" data-sky={glance.night ? "night" : "day"}
      data-teach-scene={glance.away ? "away" : glance.asleep ? "asleep" : canPlay ? "move" : "still"}>
      <div className="ps-teach-scene__frame">
      <div className="ps-teach-scene__stage" style={{ aspectRatio: layout.aspect }} aria-hidden="true">
        <img className="ps-teach-scene__room" src={plate.src} alt="" draggable={false} style={pct(layout.plate)} />
        {showFigure && summary ? (
          <div className={`ps-teach-scene__figure${glance.asleep ? " is-asleep" : ""}`} style={pct(layout.figure)}>
            {glance.asleep ? <span className="ps-teach-scene__zzz"><i>z</i><i>z</i><i>z</i></span> : null}
            <PetMotion
              pet={summary}
              state={character}
              motion={canPlay ? (motion.data as unknown as MotionSet) : null}
              initialState={clips?.basePose}
              target={canPlay && clips ? target ?? clips.basePose : undefined}
              resetKey={move?.order_id ?? pet.petId}
              requiredClipKeys={canPlay && clips ? [clips.idle.clip_key, clips.enter.clip_key, clips.main.clip_key, clips.exit.clip_key] : undefined}
              waitForLoad
            />
          </div>
        ) : null}
      </div>
      {canPlay && title ? <button type="button" className="ps-teach-scene__tap" aria-label={SCENE_AGAIN(pet.name, title)} onClick={play} data-teach-scene-play /> : null}
      </div>
      <p className="ps-teach-scene__caption">{caption}</p>
      <p className="visually-hidden" role="status">{doing}</p>
    </section>
  );
}
